"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Send, User, Phone, Mail, MessageSquare } from "lucide-react";
import { submitContactForm } from "@/app/actions/contactSubmit";

interface TreatmentFormProps {
      title: string;
      description?: string;
      className?: string;
      id?: string;
      sourceUrl?: string;
}

export default function TreatmentForm({
      title,
      description = "Aşağıdaki formu doldurun, tedavi hakkında size özel bilgi verelim.",
      className,
      id,
      sourceUrl,
}: TreatmentFormProps) {
      const router = useRouter();
      const [formData, setFormData] = useState({ name: "", number: "", email: "", mesaji: "" });
      const [isSubmitting, setIsSubmitting] = useState(false);

      const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setIsSubmitting(true);

            try {
                  const result = await submitContactForm({
                        name: formData.name.trim(),
                        number: formData.number.trim(),
                        email: formData.email.trim(),
                        mesaji: formData.mesaji.trim(),
                        sourceUrl: sourceUrl || "https://wolisticcenter.com",
                  });

                  if (!result.success) throw new Error(result.message);

                  router.push("/tesekkurler");
            } catch (err) {
                  console.error(err);
                  alert("Bir hata oluştu, lütfen tekrar deneyin.");
            } finally {
                  setIsSubmitting(false);
            }
      };

      return (
            <div id={id} className={`bg-white rounded-2xl md:rounded-[2rem] p-5 md:p-8 shadow-xl border border-gray-100 relative overflow-hidden transition-all duration-500 scroll-mt-32 ${className}`}>
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#A68F72] to-[#D4C5A9]" />

                  <div className="mb-8">
                        <h3 className="text-2xl font-bold text-[#404041] mb-2 leading-tight">
                              {title}
                        </h3>
                        <p className="text-gray-500 text-sm font-light leading-relaxed">
                              {description}
                        </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="relative group">
                              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#A68F72] transition-colors">
                                    <User size={18} />
                              </div>
                              <input
                                    required
                                    type="text"
                                    placeholder="Adınız Soyadınız"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-[#faf9f6] border border-transparent focus:bg-white focus:border-[#A68F72] focus:ring-2 focus:ring-[#A68F72]/20 transition-all outline-none text-sm placeholder:text-gray-400 text-gray-700"
                              />
                        </div>

                        <div className="relative group">
                              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#A68F72] transition-colors">
                                    <Phone size={18} />
                              </div>
                              <input
                                    required
                                    type="tel"
                                    placeholder="Telefon Numaranız"
                                    value={formData.number}
                                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-[#faf9f6] border border-transparent focus:bg-white focus:border-[#A68F72] focus:ring-2 focus:ring-[#A68F72]/20 transition-all outline-none text-sm placeholder:text-gray-400 text-gray-700"
                              />
                        </div>

                        <div className="relative group">
                              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#A68F72] transition-colors">
                                    <Mail size={18} />
                              </div>
                              <input
                                    type="email"
                                    placeholder="E-Posta Adresiniz"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-[#faf9f6] border border-transparent focus:bg-white focus:border-[#A68F72] focus:ring-2 focus:ring-[#A68F72]/20 transition-all outline-none text-sm placeholder:text-gray-400 text-gray-700"
                              />
                        </div>

                        <div className="relative group">
                              <div className="absolute left-4 top-4 text-gray-400 group-focus-within:text-[#A68F72] transition-colors">
                                    <MessageSquare size={18} />
                              </div>
                              <textarea
                                    rows={3}
                                    placeholder="Sorunuz veya notunuz..."
                                    value={formData.mesaji}
                                    onChange={(e) => setFormData({ ...formData, mesaji: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-[#faf9f6] border border-transparent focus:bg-white focus:border-[#A68F72] focus:ring-2 focus:ring-[#A68F72]/20 transition-all outline-none text-sm placeholder:text-gray-400 text-gray-700 resize-none"
                              />
                        </div>

                        <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full bg-[#A68F72] text-white font-bold py-4 rounded-xl hover:bg-[#8f7a61] hover:-translate-y-0.5 active:scale-95 transition-all shadow-lg shadow-[#A68F72]/30 flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                              {isSubmitting ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                    <>Randevu Oluştur <Send size={18} /></>
                              )}
                        </button>

                        <p className="text-[10px] text-gray-400 text-center mt-3 leading-tight">
                              Kişisel verileriniz KVKK kapsamında korunmaktadır.
                        </p>
                  </form>
            </div>
      );
}
